import { useSession } from "@clerk/clerk-react";
import { ChevronRightIcon, Link } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import Icon, { ICONS } from "@/components/Core/Icons";
import { DueDateBadge } from "@/components/common/badges/due-date-badge";
import { EstimateBadge } from "@/components/common/badges/estimate-badge";
import { LabelBadge } from "@/components/common/badges/label-badge";
import { PriorityBadge } from "@/components/common/badges/priority-badge";
import {
  ShortcutHandler,
  useShortcuts,
} from "@/components/providers/ShortcutProvider";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { useBuildProjectLink } from "@/hooks/useProjectWithUsers";
import { cn } from "@/lib/utils";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { useProjectModalStore } from "@/store/zustand/projectModalStore";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import type { FeaturePRD } from "@/types/issues";
import type { LabelInterface } from "@/types/labels";
import { generateJsonStream } from "@/utils/ai/utils";
import { GitProvider } from "@/utils/gitProviderUtils";
import { TEMPLATE_PROJECT_PATHS } from "@/utils/templateProjects";
import type { Canvas, Project, ProjectWithStatuses } from "@/utils/types";
import {
  fixJson,
  getAxiosErorrMessage,
  getSafeRepositoryUrl,
  getUnexpiredToken,
} from "@/utils/utils";
import { PRD_CONSTANTS } from "./AI/PRD/constants";
import { SyncedTipTapEditor } from "./AI/PRD/SyncedTipTapEditor";
import CreateRepositoryModal from "./CreateRepositoryModal";
import { StatusBadge } from "./common/badges/status-badge";
import AssigneeDropdown from "./common/issues/assignee-dropdown";
import NoRepositoryModal from "./NoRepositoryModal";
import { useSharedStatusManagement } from "./ProjectConfiguration/hooks";
import { KanbanDefaultStates } from "./ProjectList/CanvasKanban";
import type { FilterState } from "./ProjectList/types";
import { Separator } from "./ui/separator";
import { Textarea } from "./ui/textarea";

interface Props {
  openCanvasState: string | null;
  setOpenCanvasState: (state: string | null) => void;
  project: Project | null;
  createIndex: number;
  filters?: FilterState;
}

const CreateCanvasModal: React.FC<Props> = ({
  openCanvasState,
  setOpenCanvasState,
  project,
  createIndex,
  filters,
}) => {
  const initialFormState = {
    creating: false,
    featureName: "",
    featureDescription: "",
    canvasDetails: {
      labels: [] as string[],
      assigned_user_id: null as string | null,
    },
    prdDetails: {
      priority: null as number | null,
      estimate: null as number | null,
      dueDate: null as Date | null,
      status: KanbanDefaultStates.BACKLOG.valueOf(),
    },
  };

  const [formState, setFormState] = useState(initialFormState);
  const [isBlinking, setIsBlinking] = useState(false);
  const [showNoRepoModal, setShowNoRepoModal] = useState(false);
  const [showCreateRepoModal, setShowCreateRepoModal] = useState(false);
  const [originatingGitProvider, setOriginatingGitProvider] =
    useState<GitProvider | null>(null);
  const titleInputRef = useRef<HTMLTextAreaElement>(null);

  const { session } = useSession();
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const buildLink = useBuildProjectLink();

  const navigate = useNavigate();

  const { createFeaturePRD, createCanvas: createCanvasInStore } =
    useIssuesStore();

  const editorRef = useRef<any>(null);

  const { comprehensiveStatusList } = useSharedStatusManagement({
    customProjectStatuses: (project as ProjectWithStatuses)?.statuses || [],
  });

  useEffect(() => {
    if (openCanvasState) {
      setFormState((prevState) => ({
        ...prevState,
        prdDetails: {
          ...prevState.prdDetails,
          status: openCanvasState,
        },
        canvasDetails: {
          ...prevState.canvasDetails,
          labels: filters?.labels || [],
          assigned_user_id:
            filters?.userIds?.length === 1
              ? filters.userIds.at(0) || null
              : null,
        },
      }));
    }
  }, [openCanvasState, filters]);

  // Check if project has a repo
  const hasRepo = useMemo(() => {
    if (project && typeof project.repoPath === "string") {
      const repoPath = project.repoPath.toLowerCase();
      // Check if project has a real repository (not a template)
      const isTemplateProject = TEMPLATE_PROJECT_PATHS.some(
        (templatePath) => templatePath === repoPath,
      );
      return !isTemplateProject;
    }
    return false;
  }, [project]);

  const handleCreateRepository = (
    provider: "github" | "gitlab" | "bitbucket",
  ) => {
    let gitProvider: GitProvider | null = null;
    if (provider === "github") {
      gitProvider = GitProvider.GITHUB;
    } else if (provider === "gitlab") {
      gitProvider = GitProvider.GITLAB;
    } else if (provider === "bitbucket") {
      gitProvider = GitProvider.BITBUCKET;
    }
    setOriginatingGitProvider(gitProvider);
    setShowNoRepoModal(false);
    setShowCreateRepoModal(true);
  };

  const validateAndAction = (): boolean => {
    if (!formState.featureName) {
      toast.error("Title is required");
      return false;
    }
    return true;
  };

  const createCanvas = async () => {
    if (!project) {
      toast.error("Project is required");
      return;
    }

    const canvasPayload: Partial<Canvas> = {
      assigned_user_id: formState.canvasDetails.assigned_user_id,
      labels: formState.canvasDetails.labels,
    };

    const supabaseJWT = await getUnexpiredToken(session);

    const canvas = await createCanvasInStore(
      supabaseClient,
      supabaseJWT || "",
      project.id,
      canvasPayload as Canvas,
    );

    return canvas;
  };

  const savePRD = async (canvasId: string) => {
    if (!canvasId) {
      toast.error("Canvas ID is missing. Cannot save PRD.");
      return;
    }

    const newPrd: Partial<FeaturePRD> = {
      id: uuidv4().toString(),
      canvas_id: canvasId,
      status: formState.prdDetails.status,
      order: createIndex,
      title: formState.featureName,
      prd: formState.featureDescription,
      priority: formState.prdDetails.priority ?? undefined,
      estimate: formState.prdDetails.estimate ?? undefined,
      due_date: formState.prdDetails.dueDate?.toISOString() ?? undefined,
      created_at: new Date().toISOString(),
    };

    createFeaturePRD(supabaseClient, newPrd as FeaturePRD);

    return newPrd;
  };

  const handleSuccess = (canvas: any) => {
    toast("New canvas created", {
      action: {
        label: "Open",
        onClick: () => {
          const link = buildLink(project, canvas);
          navigate(link);
        },
      },
    });

    setOpenCanvasState(null);
    setFormState(initialFormState);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.currentTarget.value;
    setFormState((prevState) => ({
      ...prevState,
      featureName: value,
    }));
  };

  const onSubmit = useCallback(async (): Promise<void> => {
    if (formState.creating) {
      return;
    }

    if (!validateAndAction()) return;

    if (!hasRepo) {
      setShowNoRepoModal(true);
      return;
    }

    setFormState((prevState) => ({ ...prevState, creating: true }));

    try {
      const createdCanvas = await createCanvas();
      setFormState((prevState) => ({
        ...prevState,
        canvasDetails: createdCanvas,
      }));

      await savePRD(createdCanvas?.id);

      handleSuccess(createdCanvas);
    } catch (err) {
      const message = getAxiosErorrMessage(err);
      toast.error(`Error creating canvas: ${message}`);
    } finally {
      setFormState((prevState) => ({ ...prevState, creating: false }));
    }
  }, [
    hasRepo,
    formState,
    createIndex,
    project,
    session,
    supabaseClient,
    buildLink,
    navigate,
    createFeaturePRD,
    createCanvasInStore,
    formState,
  ]);

  const validateAiEnhancePayload = () => {
    if (!formState.featureName && !editorRef.current?.getText()) {
      return false;
    }
    return true;
  };

  const aiEnhanceDescription = useCallback(async () => {
    if (!session) {
      return;
    }

    try {
      setIsBlinking(true);
      const url = `${
        import.meta.env.VITE_BACKEND_BASE_URL
      }/canvases/dummy-id/ai/vercel-ai/aiEnhanceDescription`;
      const body = {
        title: formState.featureName,
        description: formState.featureDescription,
      };

      const supabaseJWT = await getUnexpiredToken(session);

      const stream = await generateJsonStream(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      setFormState((prevState) => ({
        ...prevState,
        featureDescription: "",
      }));

      let partialResult = "";
      let res = null;

      for await (const chunk of stream) {
        if (chunk.status === -1 && chunk.message === "intermediate") {
          const parsedChunk = JSON.parse(chunk.data);

          if (parsedChunk.type === "text-delta") {
            const enhancedDescription = parsedChunk.chunk;
            partialResult += enhancedDescription;
          }
          setFormState((prevState) => ({
            ...prevState,
            featureDescription:
              fixJson(partialResult).fixedObj?.enhancedDescription || "",
          }));
        } else {
          res = chunk;
          break;
        }
      }

      // @ts-expect-error TS18047
      if (res.status >= 400) {
        console.error({
          // @ts-expect-error TS18047
          msg: res.message,
          // @ts-expect-error TS18047
          internalErrorStr: res.internalErrorStr,
        });
        // @ts-expect-error TS18047
        throw new Error(res.message);
      }
    } catch (err) {
      toast.error(`Error enhancing description: ${err.message}`);
    } finally {
      setIsBlinking(false);
    }
  }, [formState.featureName, formState.featureDescription, Boolean(session)]);

  const handleSubmit = useCallback(
    (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onSubmit();
    },
    [onSubmit],
  );

  const { registerShortcut, unregisterShortcut } = useShortcuts();

  useEffect(() => {
    if (openCanvasState) {
      registerShortcut("mod+enter", handleSubmit, {
        preventDefault: true,
        enabled: true,
        global: true,
      });

      return () => {
        unregisterShortcut("mod+enter");
      };
    }
  }, [openCanvasState, registerShortcut, unregisterShortcut, handleSubmit]);

  const handleRepositoryClick = () => {
    const projectData = project as any;
    const safeUrl = getSafeRepositoryUrl(
      projectData?.provider || "github",
      projectData?.repoPath,
    );

    if (safeUrl) {
      window.open(safeUrl, "_blank", "noopener,noreferrer");
    } else {
      toast.error("Invalid repository path");
    }
  };

  if (!project) return null;

  return (
    <>
      <Dialog
        open={openCanvasState !== null}
        onOpenChange={(open) => {
          if (!open) {
            setOpenCanvasState(null);
            useProjectModalStore
              .getState()
              .removeProjectModal("create-canvas-modal");
          }
        }}
      >
        <ShortcutHandler enabled={openCanvasState !== null}>
          <DialogContent
            hideClose={false}
            className="sm:max-w-[425px] md:min-w-[960px] bg-background-primary border border-border-primary p-0 gap-0 top-98 -translate-y-88"
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              titleInputRef.current?.focus();
            }}
            onCloseButtonClick={() => {
              setFormState(initialFormState);
              setOpenCanvasState(null);
            }}
            onClick={(e) => {
              // Only prevent default behavior for clicks on the dialog background
              // Allow dropdowns and other interactive elements to work normally
              if (e.target === e.currentTarget) {
                e.stopPropagation();
                e.preventDefault();
              }
            }}
          >
            <DialogHeader className="px-5 py-4 border-b border-border-primary">
              <div className="text-xs leading-none gap-1.5 flex flex-row justify-start items-start py-2 w-full max-w-full truncate">
                {project?.repoPath && (
                  <div
                    className="truncate shrink max-w-48 text-text-secondary"
                    title={project.repoPath}
                  >
                    {project.repoPath}
                  </div>
                )}
                <Link
                  className="w-3 h-3 text-text-primary cursor-pointer"
                  onClick={handleRepositoryClick}
                />
              </div>
            </DialogHeader>
            <div className="grid grid-cols-4 gap-4 max-h-[calc(100vh-12rem)] ">
              <div className="px-7 w-full max-w-full overflow-x-hidden col-span-3 py-4">
                <Textarea
                  ref={titleInputRef}
                  id="name"
                  className={cn(
                    "h-auto min-h-6 font-medium bg-background-primary border-none focus:outline-hidden focus:ring-0 grow resize-none text-base leading-5 placeholder-text-muted! p-1 px-0 focus-visible:ring-0 mb-1",
                  )}
                  placeholder="Canvas Title"
                  value={formState.featureName}
                  onInput={handleInputChange}
                  data-testid="canvas-title-input"
                  onKeyDown={(e) => {
                    const isCtrlPressed = e.ctrlKey || e.metaKey;
                    if (e.key === "Enter" && !isCtrlPressed) {
                      e.preventDefault();
                      editorRef.current?.focus();
                    }
                  }}
                  rows={1}
                  maxLength={PRD_CONSTANTS.MAX_TITLE_LENGTH}
                  autoResize={true}
                />
                <SyncedTipTapEditor
                  ref={editorRef}
                  classnames="h-full py-3 px-0 text-sm! leading-5! w-full max-w-full wrap-break-word"
                  placeholder={"Write description..."}
                  valueInSupabase={formState.featureDescription || ""}
                  onUpdateValueInSupabase={(newValue) => {
                    setFormState((prevState) => ({
                      ...prevState,
                      featureDescription: newValue,
                    }));
                  }}
                />
              </div>
              <div className="flex flex-col items-start gap-4 text-xs sm:mb-0 mb-1 px-4 col-span-1 border-l border-border-primary py-4 min-h-80">
                <div className="flex flex-col items-start gap-1">
                  <span className="text-2xs font-medium text-text-secondary mb-0.5">
                    Properties
                  </span>
                  <AssigneeDropdown
                    users={project.users as any[]}
                    value={formState.canvasDetails.assigned_user_id}
                    onChange={(userId) =>
                      setFormState((prevState) => ({
                        ...prevState,
                        canvasDetails: {
                          ...prevState.canvasDetails,
                          assigned_user_id: userId,
                        },
                      }))
                    }
                    variant="text-dark"
                    borderless={true}
                  />
                  <StatusBadge
                    statuses={comprehensiveStatusList}
                    status={
                      comprehensiveStatusList.find(
                        (s) => s.name === formState.prdDetails.status,
                      ) ?? null
                    }
                    onChange={(status) => {
                      setFormState((prevState) => ({
                        ...prevState,
                        prdDetails: {
                          ...prevState.prdDetails,
                          status: status.name,
                        },
                      }));
                    }}
                    issueId={""}
                    variant="text-dark"
                    className="h-7! bg-secondary-0 text-text-primary! text-xs font-medium! w-full *:w-full"
                    borderless={true}
                  />
                  <PriorityBadge
                    onPriorityChange={(priority) => {
                      setFormState((prevState) => ({
                        ...prevState,
                        prdDetails: {
                          ...prevState.prdDetails,
                          priority: priority,
                        },
                      }));
                    }}
                    priority={formState.prdDetails.priority ?? null}
                    variant="text-dark"
                    className="h-7! bg-secondary-0 text-text-primary! text-xs font-medium!"
                    borderless={true}
                  />
                  <EstimateBadge
                    iconColor="var(--secondary-n-600)"
                    estimate={formState.prdDetails.estimate ?? null}
                    onEstimateChange={(estimate) => {
                      setFormState((prevState) => ({
                        ...prevState,
                        prdDetails: {
                          ...prevState.prdDetails,
                          estimate: estimate,
                        },
                      }));
                    }}
                    variant="text-dark"
                    className="h-7! bg-secondary-0 text-text-primary! text-xs font-medium!"
                    borderless={true}
                  />
                  <DueDateBadge
                    iconColor="var(--secondary-n-600)"
                    dueDate={formState.prdDetails.dueDate ?? null}
                    onDueDateChange={(dueDate) => {
                      setFormState((prevState) => ({
                        ...prevState,
                        prdDetails: { ...prevState.prdDetails, dueDate },
                      }));
                    }}
                    ignoreOverdueIcon={true}
                    variant="text-dark"
                    className="h-7! bg-secondary-0 text-text-primary! text-xs font-medium!"
                    borderless={true}
                  />
                </div>
                <div className="flex flex-col items-start gap-1">
                  <span className="text-2xs font-medium text-text-secondary mb-0.5">
                    Labels
                  </span>
                  <LabelBadge
                    iconColor="var(--secondary-n-600)"
                    className="h-7! bg-secondary-0 text-text-primary! font-medium! px-1 text-xxs"
                    projectLabels={
                      project.labels as unknown as LabelInterface[]
                    }
                    labelIdsFallback={
                      formState.canvasDetails.labels as unknown as string[]
                    }
                    labelIdsToStackFallback={
                      formState.canvasDetails.labels as unknown as string[]
                    }
                    onChange={(newLabels) => {
                      setFormState((prevState) => ({
                        ...prevState,
                        canvasDetails: {
                          ...prevState.canvasDetails,
                          labels: newLabels as never[],
                        },
                      }));
                    }}
                    title="Add Label"
                  />
                </div>
              </div>
            </div>
            <Separator className="space-y-0 m-0!" />
            <div className="flex flex-row justify-between items-center px-4 py-4 bg-background rounded-b-lg z-10">
              <div className="flex items-center gap-2 text-xs size-7 shrink-0">
                <Button
                  size="icon"
                  data-tooltip-id="tooltip"
                  data-tooltip-content={"AI"}
                  className={cn(
                    "relative! h-7! w-7! text-xs! rounded-md! p-0!  !hover:bg-border-secondary-n-900 cursor-pointer bg-background-tertiary !hover:bg-border-secondary-n-900 !hover:border-0",
                    formState.creating ? "animate-pulse" : "",
                  )}
                  onClick={aiEnhanceDescription}
                  variant="outline"
                  disabled={!validateAiEnhancePayload()}
                >
                  <Icon
                    icon={ICONS.AI_LOGO}
                    className={cn(
                      "absolute! transition-colors opacity-20 group-hover:opacity-100 w-4 h-4",
                      isBlinking ? "animate-fast-pulse" : "",
                      {
                        "opacity-20": !isBlinking,
                      },
                    )}
                  />
                  <Icon
                    icon={ICONS.STAR}
                    width={6}
                    height={6}
                    className={cn(
                      "opacity-20 absolute! right-0.5 top-0.5",
                      isBlinking ? "animate-fast-pulse" : "",
                      {
                        "opacity-100": !isBlinking,
                      },
                    )}
                  />
                </Button>
              </div>
              <Button
                className="flex items-center gap-1 h-7 text-xs font-medium rounded-md px-2 py-2 w-fit"
                type="submit"
                variant="primary"
                onClick={onSubmit}
                disabled={formState.creating}
                data-testid="create-canvas-button"
              >
                {formState.creating ? "Creating..." : "Create Canvas"}
              </Button>
            </div>
          </DialogContent>
        </ShortcutHandler>
      </Dialog>
      <NoRepositoryModal
        isOpen={showNoRepoModal}
        onClose={() => setShowNoRepoModal(false)}
        onCreateRepository={handleCreateRepository}
      />
      {showCreateRepoModal && (
        <CreateRepositoryModal
          canvasId={""}
          isOpen={showCreateRepoModal}
          onClose={() => {
            setShowCreateRepoModal(false);
            setOriginatingGitProvider(null);
          }}
          onRepoCreated={() => {
            setShowCreateRepoModal(false);
            setOriginatingGitProvider(null);
          }}
          initialSelectedOrg={null}
          originatingProvider={originatingGitProvider || undefined}
        />
      )}
    </>
  );
};

export default React.memo(CreateCanvasModal);
